import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Typography,
    Box,
    Modal,
    Button,
    IconButton,
    Chip,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Select,
    MenuItem,
    Tooltip,
    Pagination,
    InputAdornment
} from '@mui/material';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

import { getData, postData, putData, patchData, deleteData } from '../../../webService/webservice.js';
import '../../../styles/Auth/package-management.css';
import TVFMLoader from '../Loader/loader.jsx';

/**
 * Utility to calculate GST Amount and Total Amount in real-time
 */
export const calculatePackageAmountClient = (packagePrice, isGstApplicable, gstPercentage, gstType) => {
    const price = Number(packagePrice) || 0;
    const gst = Number(gstPercentage) || 0;

    let gstAmount = 0;
    let totalAmount = price;

    if (isGstApplicable && gst > 0) {
        if (gstType === 'Exclusive') {
            gstAmount = (price * gst) / 100;
            totalAmount = price + gstAmount;
        } else if (gstType === 'Inclusive') {
            gstAmount = price - (price / (1 + gst / 100));
            totalAmount = price;
        }
    }

    return {
        gstAmount: Number(gstAmount.toFixed(2)),
        totalAmount: Math.round(totalAmount)
    };
};

/**
 * Utility to calculate and format total validity string in real-time
 */
export const calculateTotalValidityClient = (baseValidity, validityUnit, extraValidity = 0, extraValidityUnit = 'Days') => {
    const baseNum = Number(baseValidity) || 0;
    const extraNum = Number(extraValidity) || 0;

    if (baseNum <= 0) return "-";

    const baseUnitSingular = validityUnit ? validityUnit.replace(/s$/i, '') : 'Month';
    const baseUnitStr = baseNum === 1 ? baseUnitSingular : `${baseUnitSingular}s`;
    const baseStr = `${baseNum} ${baseUnitStr}`;

    if (extraNum <= 0) {
        return baseStr;
    }

    const extraUnitSingular = extraValidityUnit ? extraValidityUnit.replace(/s$/i, '') : 'Day';

    if (validityUnit === extraValidityUnit) {
        const total = baseNum + extraNum;
        const totalUnitStr = total === 1 ? baseUnitSingular : `${baseUnitSingular}s`;
        return `${total} ${totalUnitStr}`;
    }

    if (validityUnit === 'Years' && extraValidityUnit === 'Months') {
        const totalMonths = (baseNum * 12) + extraNum;
        return `${totalMonths} Months`;
    }

    const extraUnitStr = extraNum === 1 ? extraUnitSingular : `${extraUnitSingular}s`;
    return `${baseStr} ${extraNum} ${extraUnitStr}`;
};

const PackageManagementPage = () => {
    const navigate = useNavigate();
    const role = localStorage.getItem("role") || sessionStorage.getItem("role");

    // 🔒 Auth Guard: SuperAdmin and SelfAdmin only
    const isAuthorized = role === "SuperAdmin" || role === "SelfAdmin";

    const [loading, setLoading] = useState(false);
    const [packages, setPackages] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    // Stats
    const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

    // Modals
    const [openModal, setOpenModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedPackageId, setSelectedPackageId] = useState(null);

    const [openViewModal, setOpenViewModal] = useState(false);
    const [viewPackageData, setViewPackageData] = useState(null);

    // Form State
    const initialFormState = {
        packageName: '',
        description: '',
        packagePrice: '',
        isGstApplicable: false,
        gstPercentage: '',
        gstType: 'Exclusive',
        baseValidity: '',
        validityUnit: 'Months',
        extraValidity: '0',
        extraValidityUnit: 'Days',
        status: 'Active'
    };

    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState({});

    // Redirect unauthorized users
    useEffect(() => {
        if (!isAuthorized) {
            toast.error("Access Denied: Package Management is restricted to Super Admin & Self Admin.");
            navigate('/dashboard');
        }
    }, [isAuthorized, navigate]);

    // Fetch Packages API
    const fetchPackages = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: limit,
                search: searchQuery,
                status: statusFilter
            };
            const response = await getData("packages", params);
            if (response && response.status === 200 && response.data) {
                setPackages(response.data.packages || []);
                setTotalCount(response.data.totalCount || 0);
                setTotalPages(response.data.totalPages || 1);
            } else if (response && response.success === false) {
                toast.error(response.message || "Failed to fetch packages");
                setPackages([]);
            }
        } catch (error) {
            console.error("Error fetching packages:", error);
            toast.error("Error fetching package list");
            setPackages([]);
        } finally {
            setLoading(false);
        }
    }, [currentPage, limit, searchQuery, statusFilter]);

    // Fetch Stats summary
    const fetchStats = useCallback(async () => {
        try {
            const [allRes, activeRes, inactiveRes] = await Promise.all([
                getData("packages", { limit: 1 }),
                getData("packages", { limit: 1, status: "Active" }),
                getData("packages", { limit: 1, status: "Inactive" })
            ]);
            setStats({
                total: allRes?.data?.totalCount || 0,
                active: activeRes?.data?.totalCount || 0,
                inactive: inactiveRes?.data?.totalCount || 0
            });
        } catch (err) {
            console.error("Error fetching package stats:", err);
        }
    }, []);

    useEffect(() => {
        if (isAuthorized) {
            fetchPackages();
            fetchStats();
        }
    }, [isAuthorized, fetchPackages, fetchStats]);

    // Handle Form Change
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;

        setFormData(prev => {
            const updated = { ...prev, [name]: val };

            // Reset GST Percentage if GST Applicable becomes false
            if (name === 'isGstApplicable' && !val) {
                updated.gstPercentage = '';
            }

            return updated;
        });

        // Clear error for field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Client-side Validation
    const validateForm = () => {
        const newErrors = {};

        if (!formData.packageName || !formData.packageName.trim()) {
            newErrors.packageName = "Package Name is required.";
        }

        const priceNum = Number(formData.packagePrice);
        if (!formData.packagePrice || isNaN(priceNum) || priceNum <= 0) {
            newErrors.packagePrice = "Package Price is required and must be greater than 0.";
        }

        const baseValNum = Number(formData.baseValidity);
        if (!formData.baseValidity || isNaN(baseValNum) || baseValNum <= 0) {
            newErrors.baseValidity = "Base Validity is required and must be greater than 0.";
        }

        if (formData.isGstApplicable) {
            const gstNum = Number(formData.gstPercentage);
            if (formData.gstPercentage === '' || formData.gstPercentage === null || isNaN(gstNum) || gstNum < 0) {
                newErrors.gstPercentage = "GST Percentage is required when GST is applicable.";
            }
        }

        if (formData.extraValidity !== '' && formData.extraValidity !== null) {
            const extraNum = Number(formData.extraValidity);
            if (isNaN(extraNum) || extraNum < 0) {
                newErrors.extraValidity = "Extra Validity cannot be negative.";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Open Add Modal
    const handleOpenAddModal = () => {
        setIsEditMode(false);
        setSelectedPackageId(null);
        setFormData(initialFormState);
        setErrors({});
        setOpenModal(true);
    };

    // Open Edit Modal
    const handleOpenEditModal = (pkg) => {
        setIsEditMode(true);
        setSelectedPackageId(pkg._id);
        setFormData({
            packageName: pkg.packageName || '',
            description: pkg.description || '',
            packagePrice: pkg.packagePrice !== undefined ? String(pkg.packagePrice) : '',
            isGstApplicable: pkg.isGstApplicable || false,
            gstPercentage: pkg.gstPercentage !== undefined ? String(pkg.gstPercentage) : '',
            gstType: pkg.gstType || 'Exclusive',
            baseValidity: pkg.baseValidity !== undefined ? String(pkg.baseValidity) : '',
            validityUnit: pkg.validityUnit || 'Months',
            extraValidity: pkg.extraValidity !== undefined ? String(pkg.extraValidity) : '0',
            extraValidityUnit: pkg.extraValidityUnit || 'Days',
            status: pkg.status || 'Active'
        });
        setErrors({});
        setOpenModal(true);
    };

    // Open View Modal
    const handleOpenViewModal = (pkg) => {
        setViewPackageData(pkg);
        setOpenViewModal(true);
    };

    // Form Submission (Add or Edit)
    const handleSubmitForm = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            toast.error("Please resolve validation errors before submitting.");
            return;
        }

        const liveCalc = calculatePackageAmountClient(
            formData.packagePrice,
            formData.isGstApplicable,
            formData.gstPercentage,
            formData.gstType
        );

        const payload = {
            packageName: formData.packageName.trim(),
            description: formData.description ? formData.description.trim() : "",
            packagePrice: Number(formData.packagePrice),
            isGstApplicable: Boolean(formData.isGstApplicable),
            gstPercentage: formData.isGstApplicable ? Number(formData.gstPercentage) : 0,
            gstType: formData.gstType,
            gstAmount: liveCalc.gstAmount,
            totalAmount: liveCalc.totalAmount,
            baseValidity: Number(formData.baseValidity),
            validityUnit: formData.validityUnit,
            extraValidity: Number(formData.extraValidity) || 0,
            extraValidityUnit: formData.extraValidityUnit,
            status: formData.status
        };

        setLoading(true);
        try {
            const token = sessionStorage.getItem("Token") || localStorage.getItem("Token");
            let res;
            if (isEditMode) {
                res = await putData(`packages/${selectedPackageId}`, payload, token);
            } else {
                res = await postData("packages", payload, token);
            }

            if (res && (res.status === 200 || res.status === 201 || res.success)) {
                toast.success(isEditMode ? "Package updated successfully!" : "Package created successfully!");
                setOpenModal(false);
                fetchPackages();
                fetchStats();
            } else {
                toast.error(res?.message || "Failed to save package");
            }
        } catch (error) {
            console.error("Error submitting package form:", error);
            toast.error("An error occurred while saving package.");
        } finally {
            setLoading(false);
        }
    };

    // Toggle Package Status (Activate / Deactivate with Swal Confirmation)
    const handleToggleStatus = async (pkg) => {
        const targetStatus = pkg.status === 'Active' ? 'Inactive' : 'Active';
        const isDeactivating = targetStatus === 'Inactive';

        const result = await Swal.fire({
            icon: isDeactivating ? 'warning' : 'question',
            title: isDeactivating ? 'Deactivate Package?' : 'Activate Package?',
            html: isDeactivating
                ? `Are you sure you want to set package <b>"${pkg.packageName}"</b> to <b>Inactive</b>?<br/><br/><small style="color: #64748b;">Existing store subscriptions will remain active until expiry, but this package will no longer be available for new subscription assignments.</small>`
                : `Are you sure you want to activate package <b>"${pkg.packageName}"</b>? It will become available for store subscriptions.`,
            showCancelButton: true,
            confirmButtonText: isDeactivating ? 'Yes, Deactivate' : 'Yes, Activate',
            cancelButtonText: 'Cancel',
            confirmButtonColor: isDeactivating ? '#dc2626' : '#16a34a',
            cancelButtonColor: '#64748b'
        });

        if (result.isConfirmed) {
            setLoading(true);
            try {
                const res = await patchData(`packages/${pkg._id}/status`, { status: targetStatus });
                if (res && (res.status === 200 || res.success)) {
                    toast.success(`Package "${pkg.packageName}" marked as ${targetStatus}`);
                    fetchPackages();
                    fetchStats();
                } else {
                    toast.error(res?.message || "Failed to change status");
                }
            } catch (err) {
                console.error("Error toggling status:", err);
                toast.error("An error occurred while changing status");
            } finally {
                setLoading(false);
            }
        }
    };

    // Delete Package Handler (Soft validation on backend)
    const handleDeletePackage = async (pkg) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Delete Package?',
            text: `Are you sure you want to delete package "${pkg.packageName}"?`,
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#64748b'
        });

        if (result.isConfirmed) {
            setLoading(true);
            try {
                const token = sessionStorage.getItem("Token") || localStorage.getItem("Token");
                const res = await deleteData(`packages/${pkg._id}`, token);
                if (res && res.success) {
                    toast.success("Package deleted successfully");
                    fetchPackages();
                    fetchStats();
                } else {
                    toast.error(res?.message || "Package cannot be deleted.");
                }
            } catch (err) {
                console.error("Error deleting package:", err);
                toast.error("Error deleting package");
            } finally {
                setLoading(false);
            }
        }
    };

    // Current Computed Validity for Form Preview
    const currentComputedValidity = calculateTotalValidityClient(
        formData.baseValidity,
        formData.validityUnit,
        formData.extraValidity,
        formData.extraValidityUnit
    );

    if (!isAuthorized) {
        return null;
    }

    return (
        <div className='product-layout'>
            <div className="package-management-container">
                {loading && <TVFMLoader />}

                {/* Header */}
                <div className="package-header">
                    <div className="package-header-title">
                        <div className="package-header-icon">
                            <Icon icon="mdi:package-variant-closed" width="24" height="24" />
                        </div>
                        <div>
                            <h2>Package Management</h2>
                            <p>Manage subscription packages, pricing, GST parameters, and validity rules.</p>
                        </div>
                    </div>
                    <Button
                        variant="contained"
                        className="add-package-btn"
                        startIcon={<Icon icon="mdi:plus-circle-outline" width="20" height="20" />}
                        onClick={handleOpenAddModal}
                    >
                        Add Package
                    </Button>
                </div>

                {/* Stats Cards */}
                <div className="package-stats-grid">
                    <div className="package-stat-card">
                        <div className="stat-icon-wrapper all">
                            <Icon icon="mdi:package-variant" />
                        </div>
                        <div className="stat-info">
                            <h4>{stats.total}</h4>
                            <span>Total Master Packages</span>
                        </div>
                    </div>
                    <div className="package-stat-card">
                        <div className="stat-icon-wrapper active">
                            <Icon icon="mdi:check-circle-outline" />
                        </div>
                        <div className="stat-info">
                            <h4>{stats.active}</h4>
                            <span>Active Packages</span>
                        </div>
                    </div>
                    <div className="package-stat-card">
                        <div className="stat-icon-wrapper inactive">
                            <Icon icon="mdi:close-circle-outline" />
                        </div>
                        <div className="stat-info">
                            <h4>{stats.inactive}</h4>
                            <span>Inactive Packages</span>
                        </div>
                    </div>
                </div>

                {/* Controls Card (Search + Status Filter) */}
                <div className="package-controls-card">
                    <div className="search-box-wrapper">
                        <Icon icon="mdi:magnify" width="20" height="20" color="#64748b" />
                        <input
                            type="text"
                            placeholder="Search by package name or description..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                        {searchQuery && (
                            <IconButton size="small" onClick={() => setSearchQuery('')}>
                                <Icon icon="mdi:close" width="16" height="16" />
                            </IconButton>
                        )}
                    </div>

                    <div className="filter-group">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                            Status Filter:
                        </Typography>
                        <Select
                            size="small"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            sx={{
                                minWidth: 140,
                                borderRadius: '8px',
                                bgcolor: '#ffffff',
                                '& .MuiSelect-select': { py: 1, fontSize: '13.5px', fontWeight: 600 }
                            }}
                        >
                            <MenuItem value="All">All Statuses</MenuItem>
                            <MenuItem value="Active">Active</MenuItem>
                            <MenuItem value="Inactive">Inactive</MenuItem>
                        </Select>
                    </div>
                </div>

                {/* Packages Table */}
                <TableContainer component={Paper} className="package-table-container">
                    <Table className="package-table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Package Name</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>GST Details</TableCell>
                                <TableCell>GST Amount</TableCell>
                                <TableCell>Total Amount</TableCell>
                                <TableCell>Base Validity</TableCell>
                                <TableCell>Extra Validity</TableCell>
                                <TableCell>Total Validity</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Created Date</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {packages.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                        <Box sx={{ textAlign: 'center', color: '#94a3b8' }}>
                                            <Icon icon="mdi:package-variant-remove" width="48" height="48" style={{ opacity: 0.5, marginBottom: '8px' }} />
                                            <Typography variant="h6" sx={{ color: '#475569', fontWeight: 600, fontSize: '16px' }}>
                                                No Packages Found
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                                                {searchQuery || statusFilter !== 'All'
                                                    ? "Try adjusting your search query or status filter."
                                                    : "Click on 'Add Package' to create your first master package."}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                packages.map((pkg) => {
                                    const formattedPrice = `₹${Number(pkg.packagePrice || 0).toLocaleString('en-IN')}`;
                                    const createdDateStr = pkg.createdAt
                                        ? new Date(pkg.createdAt).toLocaleDateString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric'
                                        })
                                        : '-';

                                    return (
                                        <TableRow key={pkg._id}>
                                            <TableCell>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                                    {pkg.packageName}
                                                </Typography>
                                                {pkg.description && (
                                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.2 }}>
                                                        {pkg.description.length > 50 ? `${pkg.description.substring(0, 50)}...` : pkg.description}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#3C0008' }}>
                                                    {formattedPrice}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {pkg.isGstApplicable ? (
                                                    <span className={`gst-badge ${pkg.gstType === 'Inclusive' ? 'inclusive' : 'exclusive'}`}>
                                                        {pkg.gstPercentage}% ({pkg.gstType})
                                                    </span>
                                                ) : (
                                                    <span className="gst-badge">No GST</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                                                    {(() => {
                                                        const calc = calculatePackageAmountClient(pkg.packagePrice, pkg.isGstApplicable, pkg.gstPercentage, pkg.gstType);
                                                        const amount = pkg.gstAmount !== undefined ? pkg.gstAmount : calc.gstAmount;
                                                        return `₹${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
                                                    })()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 800, color: '#166534' }}>
                                                    {(() => {
                                                        const calc = calculatePackageAmountClient(pkg.packagePrice, pkg.isGstApplicable, pkg.gstPercentage, pkg.gstType);
                                                        const amount = pkg.totalAmount !== undefined ? pkg.totalAmount : calc.totalAmount;
                                                        return `₹${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
                                                    })()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {pkg.baseValidity} {pkg.baseValidity === 1 ? pkg.validityUnit?.replace(/s$/i, '') : pkg.validityUnit}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {pkg.extraValidity > 0 ? (
                                                    <Chip
                                                        label={`${pkg.extraValidity} ${pkg.extraValidity === 1 ? pkg.extraValidityUnit?.replace(/s$/i, '') : pkg.extraValidityUnit}`}
                                                        size="small"
                                                        sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 600, fontSize: '11.5px' }}
                                                    />
                                                ) : (
                                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                                        None
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb' }}>
                                                    {pkg.totalValidity || calculateTotalValidityClient(pkg.baseValidity, pkg.validityUnit, pkg.extraValidity, pkg.extraValidityUnit)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={pkg.status}
                                                    size="small"
                                                    className={pkg.status === 'Active' ? 'status-chip-active' : 'status-chip-inactive'}
                                                />
                                            </TableCell>
                                            <TableCell>{createdDateStr}</TableCell>
                                            <TableCell align="center">
                                                <div className="action-btn-group" style={{ justifyContent: 'center' }}>
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            size="small"
                                                            className="action-icon-btn view"
                                                            onClick={() => handleOpenViewModal(pkg)}
                                                        >
                                                            <Icon icon="mdi:eye-outline" width="18" height="18" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Edit Package">
                                                        <IconButton
                                                            size="small"
                                                            className="action-icon-btn edit"
                                                            onClick={() => handleOpenEditModal(pkg)}
                                                        >
                                                            <Icon icon="mdi:square-edit-outline" width="18" height="18" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={pkg.status === 'Active' ? 'Deactivate Package' : 'Activate Package'}>
                                                        <IconButton
                                                            size="small"
                                                            className={`action-icon-btn ${pkg.status === 'Active' ? 'toggle-active' : 'toggle-inactive'}`}
                                                            onClick={() => handleToggleStatus(pkg)}
                                                        >
                                                            <Icon
                                                                icon={pkg.status === 'Active' ? 'mdi:toggle-switch' : 'mdi:toggle-switch-off-outline'}
                                                                width="22"
                                                                height="22"
                                                                color={pkg.status === 'Active' ? '#16a34a' : '#94a3b8'}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Package">
                                                        <IconButton
                                                            size="small"
                                                            className="action-icon-btn toggle-active"
                                                            onClick={() => handleDeletePackage(pkg)}
                                                        >
                                                            <Icon icon="mdi:trash-can-outline" width="18" height="18" color="#ef4444" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination Controls */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, pb: 1, px: 2, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Show</span>
                        <select
                            value={limit >= 9999 ? 'all' : limit}
                            onChange={(e) => {
                                const val = e.target.value === 'all' ? 9999 : Number(e.target.value);
                                setLimit(val);
                                setCurrentPage(1);
                            }}
                            style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff', color: '#0f172a', fontWeight: 600, cursor: 'pointer' }}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value="all">All</option>
                        </select>
                        <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Entries</span>
                    </div>

                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                        {limit >= 9999
                            ? `Showing all ${totalCount || packages.length} packages`
                            : `Showing ${totalCount > 0 ? Math.min((currentPage - 1) * limit + 1, totalCount) : 0} to ${Math.min(currentPage * limit, totalCount)} of ${totalCount} packages`
                        }
                    </Typography>

                    {totalPages > 1 && (
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(e, page) => setCurrentPage(page)}
                            color="primary"
                            size="medium"
                            showFirstButton
                            showLastButton
                            sx={{
                                '& .MuiPaginationItem-root': { fontWeight: 600, borderRadius: '8px' },
                                '& .Mui-selected': { bgcolor: '#3C0008 !important', color: '#ffffff' }
                            }}
                        />
                    )}
                </Box>

                {/* Add / Edit Package Modal */}
                <Modal open={openModal} onClose={() => setOpenModal(false)}>
                    <Box className="package-modal-box">
                        <div className="modal-header-banner">
                            <h3>
                                <Icon icon={isEditMode ? "mdi:square-edit-outline" : "mdi:package-variant-plus"} width="22" height="22" />
                                {isEditMode ? "Edit Package Details" : "Create New Package Master"}
                            </h3>
                            <IconButton size="small" onClick={() => setOpenModal(false)} sx={{ color: '#ffffff' }}>
                                <Icon icon="mdi:close" width="20" height="20" />
                            </IconButton>
                        </div>

                        <form onSubmit={handleSubmitForm} className="modal-body-scrollable">
                            {/* Live Total Validity Display Banner */}
                            <div className="total-validity-preview-banner">
                                <div>
                                    <span className="label">Calculated Total Validity</span>
                                    <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>
                                        Base Validity + Extra Validity
                                    </div>
                                </div>
                                <span className="value">{currentComputedValidity}</span>
                            </div>

                            <div className="form-grid">
                                {/* Package Name */}
                                <div className="form-grid full-width">
                                    <TextField
                                        label="Package Name *"
                                        name="packageName"
                                        fullWidth
                                        size="small"
                                        placeholder="e.g. Package I, Standard Gold Plan"
                                        value={formData.packageName}
                                        onChange={handleInputChange}
                                        error={Boolean(errors.packageName)}
                                        helperText={errors.packageName}
                                    />
                                </div>

                                {/* Description */}
                                <div className="form-grid full-width">
                                    <TextField
                                        label="Description"
                                        name="description"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        size="small"
                                        placeholder="Add brief details about what this package offers..."
                                        value={formData.description}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                {/* Package Price */}
                                <div>
                                    <TextField
                                        label="Package Price (₹) *"
                                        name="packagePrice"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        placeholder="e.g. 1499"
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                        }}
                                        value={formData.packagePrice}
                                        onChange={handleInputChange}
                                        error={Boolean(errors.packagePrice)}
                                        helperText={errors.packagePrice}
                                    />
                                </div>

                                {/* Status */}
                                <div>
                                    <FormControl fullWidth size="small">
                                        <FormLabel sx={{ fontSize: '12px', fontWeight: 600, mb: 0.5 }}>Package Status</FormLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                        >
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="Inactive">Inactive</MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>

                                {/* GST Applicable Toggle */}
                                <div className="form-grid full-width" style={{ marginTop: '4px' }}>
                                    <FormControl component="fieldset">
                                        <FormLabel sx={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                                            GST Applicable? *
                                        </FormLabel>
                                        <RadioGroup
                                            row
                                            name="isGstApplicable"
                                            value={formData.isGstApplicable ? "yes" : "no"}
                                            onChange={(e) => {
                                                const isYes = e.target.value === "yes";
                                                setFormData(prev => ({
                                                    ...prev,
                                                    isGstApplicable: isYes,
                                                    gstPercentage: isYes ? (prev.gstPercentage || '18') : ''
                                                }));
                                                if (errors.gstPercentage) {
                                                    setErrors(prev => ({ ...prev, gstPercentage: '' }));
                                                }
                                            }}
                                        >
                                            <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                                            <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                                        </RadioGroup>
                                    </FormControl>
                                </div>

                                {/* Conditional GST Fields */}
                                {formData.isGstApplicable && (
                                    <>
                                        <div>
                                            <TextField
                                                label="GST Percentage (%) *"
                                                name="gstPercentage"
                                                type="number"
                                                fullWidth
                                                size="small"
                                                placeholder="e.g. 18"
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                }}
                                                value={formData.gstPercentage}
                                                onChange={handleInputChange}
                                                error={Boolean(errors.gstPercentage)}
                                                helperText={errors.gstPercentage}
                                            />
                                        </div>
                                        <div>
                                            <FormControl fullWidth size="small">
                                                <FormLabel sx={{ fontSize: '12px', fontWeight: 600, mb: 0.5 }}>GST Type</FormLabel>
                                                <Select
                                                    name="gstType"
                                                    value={formData.gstType}
                                                    onChange={handleInputChange}
                                                >
                                                    <MenuItem value="Exclusive">Exclusive (Price + GST)</MenuItem>
                                                    <MenuItem value="Inclusive">Inclusive (Price includes GST)</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </>
                                )}

                                {/* Auto-Calculated Read-Only GST Amount & Total Amount */}
                                {(() => {
                                    const liveCalc = calculatePackageAmountClient(
                                        formData.packagePrice,
                                        formData.isGstApplicable,
                                        formData.gstPercentage,
                                        formData.gstType
                                    );

                                    return (
                                        <>
                                            <div>
                                                <TextField
                                                    label="GST Amount (Auto-Calculated)"
                                                    fullWidth
                                                    size="small"
                                                    value={`₹${liveCalc.gstAmount.toFixed(2)}`}
                                                    InputProps={{ readOnly: true }}
                                                    sx={{ bgcolor: '#f8fafc' }}
                                                />
                                            </div>
                                            <div>
                                                <TextField
                                                    label="Total Amount (Auto-Calculated)"
                                                    fullWidth
                                                    size="small"
                                                    value={`₹${liveCalc.totalAmount.toFixed(2)}`}
                                                    InputProps={{ readOnly: true }}
                                                    sx={{ bgcolor: '#f8fafc', '& .MuiInputBase-input': { fontWeight: 800, color: '#3C0008' } }}
                                                />
                                            </div>
                                        </>
                                    );
                                })()}

                                {/* Base Validity */}
                                <div>
                                    <TextField
                                        label="Base Validity *"
                                        name="baseValidity"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        placeholder="e.g. 1, 6, 12"
                                        value={formData.baseValidity}
                                        onChange={handleInputChange}
                                        error={Boolean(errors.baseValidity)}
                                        helperText={errors.baseValidity}
                                    />
                                </div>

                                {/* Base Validity Unit */}
                                <div>
                                    <FormControl fullWidth size="small">
                                        <FormLabel sx={{ fontSize: '12px', fontWeight: 600, mb: 0.5 }}>Validity Unit *</FormLabel>
                                        <Select
                                            name="validityUnit"
                                            value={formData.validityUnit}
                                            onChange={handleInputChange}
                                        >
                                            <MenuItem value="Days">Days</MenuItem>
                                            <MenuItem value="Months">Months</MenuItem>
                                            <MenuItem value="Years">Years</MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>

                                {/* Extra Validity / Offer */}
                                <div>
                                    <TextField
                                        label="Extra Validity / Offer"
                                        name="extraValidity"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        placeholder="e.g. 15, 1"
                                        value={formData.extraValidity}
                                        onChange={handleInputChange}
                                        error={Boolean(errors.extraValidity)}
                                        helperText={errors.extraValidity || "Optional bonus validity"}
                                    />
                                </div>

                                {/* Extra Validity Unit */}
                                <div>
                                    <FormControl fullWidth size="small">
                                        <FormLabel sx={{ fontSize: '12px', fontWeight: 600, mb: 0.5 }}>Extra Validity Unit</FormLabel>
                                        <Select
                                            name="extraValidityUnit"
                                            value={formData.extraValidityUnit}
                                            onChange={handleInputChange}
                                        >
                                            <MenuItem value="Days">Days</MenuItem>
                                            <MenuItem value="Months">Months</MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>
                            </div>

                            <div className="modal-footer-actions" style={{ marginTop: '24px' }}>
                                <Button variant="outlined" color="inherit" onClick={() => setOpenModal(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={{ bgcolor: '#3C0008 !important', '&:hover': { bgcolor: '#5c000c !important' } }}
                                >
                                    {isEditMode ? "Save Changes" : "Create Package"}
                                </Button>
                            </div>
                        </form>
                    </Box>
                </Modal>

                {/* View Package Details Modal */}
                <Modal open={openViewModal} onClose={() => setOpenViewModal(false)}>
                    <Box className="package-modal-box" sx={{ maxWidth: 550 }}>
                        <div className="modal-header-banner">
                            <h3>
                                <Icon icon="mdi:package-variant-closed" width="22" height="22" />
                                Package Master Details
                            </h3>
                            <IconButton size="small" onClick={() => setOpenViewModal(false)} sx={{ color: '#ffffff' }}>
                                <Icon icon="mdi:close" width="20" height="20" />
                            </IconButton>
                        </div>

                        {viewPackageData && (
                            <div className="modal-body-scrollable">
                                <Box sx={{ textAlign: 'center', pb: 2, borderBottom: '1px solid #e2e8f0', mb: 2 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                        {viewPackageData.packageName}
                                    </Typography>
                                    <Typography variant="h4" sx={{ color: '#3C0008', fontWeight: 800, mt: 1 }}>
                                        ₹{Number(viewPackageData.packagePrice || 0).toLocaleString('en-IN')}
                                    </Typography>
                                    {viewPackageData.description && (
                                        <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
                                            {viewPackageData.description}
                                        </Typography>
                                    )}
                                </Box>

                                <div className="form-grid" style={{ gap: '14px' }}>
                                    <Box sx={{ p: 1.5, bg: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                            GST Applicable
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                            {viewPackageData.isGstApplicable ? `Yes (${viewPackageData.gstPercentage}% ${viewPackageData.gstType})` : "No"}
                                        </Typography>
                                    </Box>

                                    {(() => {
                                        const calc = calculatePackageAmountClient(
                                            viewPackageData.packagePrice,
                                            viewPackageData.isGstApplicable,
                                            viewPackageData.gstPercentage,
                                            viewPackageData.gstType
                                        );
                                        const gstAmt = viewPackageData.gstAmount !== undefined ? viewPackageData.gstAmount : calc.gstAmount;
                                        const totalAmt = viewPackageData.totalAmount !== undefined ? viewPackageData.totalAmount : calc.totalAmount;

                                        return (
                                            <>
                                                <Box sx={{ p: 1.5, bg: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                                        GST Amount
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569' }}>
                                                        ₹{Number(gstAmt).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ p: 1.5, bg: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                                        Total Amount
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#166534' }}>
                                                        ₹{Number(totalAmt).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                    </Typography>
                                                </Box>
                                            </>
                                        );
                                    })()}

                                    <Box sx={{ p: 1.5, bg: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                            Status
                                        </Typography>
                                        <div>
                                            <Chip
                                                label={viewPackageData.status}
                                                size="small"
                                                className={viewPackageData.status === 'Active' ? 'status-chip-active' : 'status-chip-inactive'}
                                                sx={{ mt: 0.2 }}
                                            />
                                        </div>
                                    </Box>

                                    <Box sx={{ p: 1.5, bg: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                            Base Validity
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                            {viewPackageData.baseValidity} {viewPackageData.validityUnit}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ p: 1.5, bg: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                            Extra Validity / Offer
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                            {viewPackageData.extraValidity > 0 ? `${viewPackageData.extraValidity} ${viewPackageData.extraValidityUnit}` : "None"}
                                        </Typography>
                                    </Box>

                                    <Box className="form-grid full-width" sx={{ p: 2, bg: '#eff6ff', borderRadius: '10px', border: '1px solid #bfdbfe' }}>
                                        <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 700, textTransform: 'uppercase' }}>
                                            Total Validity
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e3a8a' }}>
                                            {viewPackageData.totalValidity || calculateTotalValidityClient(viewPackageData.baseValidity, viewPackageData.validityUnit, viewPackageData.extraValidity, viewPackageData.extraValidityUnit)}
                                        </Typography>
                                    </Box>
                                </div>

                                <div className="modal-footer-actions" style={{ marginTop: '20px', padding: 0, background: 'transparent', border: 'none' }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => setOpenViewModal(false)}
                                        sx={{ bgcolor: '#3C0008 !important' }}
                                    >
                                        Close Details
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Box>
                </Modal>
            </div>
        </div>

    );
};

export default PackageManagementPage;
